import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { tl } from "@/lib/i18nFormat";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon, Store, User, BookOpen, GraduationCap, LifeBuoy } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

interface Result { type: string; module: string; id: string; title: string; subtitle?: string; link: string; }

const icon: Record<string, React.ComponentType<{ size?: number }>> = {
  store: Store, user: User, knowledge: BookOpen, course: GraduationCap, ticket: LifeBuoy,
};

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [results, setResults] = useState<Result[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const run = useCallback((term: string) => {
    if (term.trim().length < 2) { setResults([]); setNote("Enter at least 2 characters"); setSearched(true); return; }
    setLoading(true);
    api.get("/search", { params: { q: term } })
      .then((r) => { setResults(r.data.results || []); setNote(r.data.note || null); })
      .catch(() => { setResults([]); setNote("Search unavailable"); })
      .finally(() => { setLoading(false); setSearched(true); });
  }, []);

  useEffect(() => { if (initial) run(initial); }, [initial, run]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setParams(q ? { q } : {});
    run(q);
  };

  const grouped = results.reduce<Record<string, Result[]>>((acc, r) => {
    (acc[r.module] ||= []).push(r);
    return acc;
  }, {});

  return (
    (<div className="space-y-6" data-testid="search-page">
      <PageHeader title="Search" subtitle="Find stores, people, policies, courses and tickets you're allowed to see." />
      <form onSubmit={submit} className="relative max-w-2xl">
        <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("Search across the platform…")}
          aria-label={t("common.search_platform")}
          className="pl-10"
          data-testid="search-input"
        />
      </form>
      {loading && <div className="text-sm text-muted-foreground" data-testid="search-loading">{t("Searching…")}</div>}
      {!loading && searched && results.length === 0 && (
        <div className="text-sm text-muted-foreground" data-testid="search-empty">
          {note || tl("No results for “{q}”.", { q: params.get("q") || q })}
        </div>
      )}
      <div className="space-y-6">
        {Object.entries(grouped).map(([module, items]) => (
          <div key={module} data-testid={`search-group-${module.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{module}</div>
            <Card className="divide-y divide-border">
              {items.map((r) => {
                const Ico = icon[r.type] || SearchIcon;
                return (
                  <button
                    key={`${r.type}-${r.id}`}
                    onClick={() => navigate(r.link)}
                    data-testid="search-result"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary"
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-md bg-secondary text-muted-foreground">
                      <Ico size={16} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">{r.title}</span>
                      {r.subtitle && <span className="block truncate text-xs text-muted-foreground">{r.subtitle}</span>}
                    </span>
                  </button>
                );
              })}
            </Card>
          </div>
        ))}
      </div>
    </div>)
  );
}
